/**
 * A room's ceiling when a mansard slopes it. Over the floor stands the room's flat ceiling, and under it the slopes of
 * the mansards on the room's walls (`src/state/attic.ts`): each a plane rising from its wall's face at the knee to the
 * ceiling across its depth, as wide as its wedge. The ceiling over a point of the floor is the lowest of them there;
 * over a window or a door that reaches above the knee the slope stays up at the opening's top (a dormer's niche).
 *
 * The floor is cut into convex cells, each under one surface — the flat ceiling, a slope or a niche's top — by the
 * lines where a surface begins, ends or meets another, so over a cell the height is one linear function of the plan
 * point. Where two slopes meet the line between them is the hip or the valley, where a slope meets the flat ceiling
 * the fold; an end of a slope or of a niche that stands free is a step, a vertical face. What has to know how high
 * the room is at a point asks this: the plan's lines and the statistics.
 *
 * Only a mansard whose top is at its room's ceiling slopes the ceiling; one hung lower, or standing free of the walls,
 * stays a piece of its own. The project keeps nothing of this: it is worked out from the mansards every time.
 */
import type { Item, Level, Room, Surface, Vec2, Wall } from '../core/hp/types';
import { contains } from '../core/geometry/edit';
import { edgeWalls, roomFloorPolygon } from '../core/geometry/roomFloor';
import { atticCuts, atticFrame, isAttic, wallFace, type AtticCut, type AtticFrame, type TagsOf } from './attic';
import { cachedModelTags, modelTagsVersion } from './modelTags';

/**
 * ANSI Z765-2021, the measure of a US home's finished area (and IRC R305.1 for a habitable room): under a sloping ceiling
 * no floor lower than 5 ft counts, and at least half of what counts must stand 7 ft high or more.
 */
export const FIVE_FEET = 1.524;
export const SEVEN_FEET = 2.1336;

/** How far a mansard's top may stand from its room's ceiling and still be part of it. */
const TOP_TOLERANCE = .05;
/**
 * A dormer's frame: its niche is this much wider than the window or door on each side and this much higher than its
 * top, so the wall keeps its reveals round the opening — a dormer's cheeks and header.
 */
export const DORMER_FRAME = .1;
const EPS = 1e-7;
/** Points closer than this, in metres, are one. */
const NEAR = 1e-9;

/** A mansard that slopes its room's ceiling. */
export interface CeilingSlope {
  item: Item;
  frame: AtticFrame & Required<Pick<AtticFrame, 'wall' | 'side'>>;
  /** Height gained per metre into the room: its height over its depth. */
  rise: number;
  /** How far into the room it runs before it meets the flat ceiling: its depth, give or take its top's tolerance. */
  reach: number;
  /** Its angle to the floor, radians. */
  pitch: number;
  /** Where a window or a door of its wall keeps it up (a dormer's niche): across `a0`–`a1`, at least `height` from the floor. */
  raises: { openingId: string; a0: number; a1: number; height: number }[];
  /** Windows lying in it, in the wedge's terms (`AtticCut`). */
  roofWindows: AtticCut[];
}

export type CeilingSurface = { kind: 'flat' } | { kind: 'slope'; slope: number } | { kind: 'raise'; slope: number; raise: number };

/** A convex piece of the floor (counter-clockwise in x–z) under one surface; there the height is `base + gx·x + gz·z`. */
export interface CeilingCell { polygon: Vec2[]; surface: CeilingSurface; base: number; gx: number; gz: number }

export interface RoomCeiling {
  room: Room;
  /** The floor inside the walls (`roomFloorPolygon`), counter-clockwise in x–z. */
  floor: Vec2[];
  /** The flat ceiling's height. */
  height: number;
  slopes: CeilingSlope[];
  cells: CeilingCell[];
}

/** A line of the plan as the function that is zero on it. */
interface Line { c: number; gx: number; gz: number }

const FLAT: CeilingSurface = { kind: 'flat' };
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;
const signedArea = (polygon: readonly Vec2[]) => polygon.reduce((sum, p, i) => { const q = polygon[(i + 1) % polygon.length]; return sum + p.x * q.z - q.x * p.z; }, 0) / 2;
const areaOf = (polygon: readonly Vec2[]) => Math.abs(signedArea(polygon));
const centroid = (polygon: readonly Vec2[]): Vec2 => {
  const area = signedArea(polygon);
  if (Math.abs(area) < 1e-12) return polygon.reduce((sum, p) => ({ x: sum.x + p.x / polygon.length, z: sum.z + p.z / polygon.length }), { x: 0, z: 0 });
  let x = 0, z = 0;
  polygon.forEach((p, i) => { const q = polygon[(i + 1) % polygon.length], k = p.x * q.z - q.x * p.z; x += (p.x + q.x) * k; z += (p.z + q.z) * k; });
  return { x: x / (6 * area), z: z / (6 * area) };
};
const value = (line: Line, p: Vec2) => line.c + line.gx * p.x + line.gz * p.z;

/** The part of a convex polygon where `line` is ≥ 0 (`keep` 1) or ≤ 0 (`keep` −1). */
function clipConvex(polygon: readonly Vec2[], line: Line, keep: 1 | -1 = 1): Vec2[] {
  const out: Vec2[] = [];
  for (let i = 0; i < polygon.length; i++) {
    const p = polygon[i], q = polygon[(i + 1) % polygon.length], fp = keep * value(line, p), fq = keep * value(line, q);
    if (fp >= -EPS) out.push(p);
    if ((fp > EPS && fq < -EPS) || (fp < -EPS && fq > EPS)) {
      const t = fp / (fp - fq);
      out.push({ x: p.x + (q.x - p.x) * t, z: p.z + (q.z - p.z) * t });
    }
  }
  return dedupe(out);
}

function dedupe(polygon: Vec2[]): Vec2[] {
  const out = polygon.filter((p, i) => { const q = polygon[(i + polygon.length - 1) % polygon.length]; return polygon.length < 2 || Math.hypot(p.x - q.x, p.z - q.z) > NEAR; });
  return out.length >= 3 && areaOf(out) > 1e-14 ? out : [];
}

/** Convex pieces of a simple polygon: itself when convex, else its ear-clipped triangles. Counter-clockwise. */
function convexPieces(polygon: readonly Vec2[]): Vec2[][] {
  const ccw = signedArea(polygon) < 0 ? [...polygon].reverse() : [...polygon];
  const turn = (a: Vec2, b: Vec2, c: Vec2) => (b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x);
  if (ccw.every((p, i) => turn(ccw[(i + ccw.length - 1) % ccw.length], p, ccw[(i + 1) % ccw.length]) >= -1e-9)) return [ccw];
  const pieces: Vec2[][] = [], left = [...ccw];
  let guard = left.length * left.length;
  while (left.length > 3 && guard-- > 0) {
    let clipped = false;
    for (let i = 0; i < left.length; i++) {
      const a = left[(i + left.length - 1) % left.length], b = left[i], c = left[(i + 1) % left.length];
      if (turn(a, b, c) <= 1e-12) continue;
      const inside = left.some(p => p !== a && p !== b && p !== c && turn(a, b, p) >= 0 && turn(b, c, p) >= 0 && turn(c, a, p) >= 0);
      if (inside) continue;
      pieces.push([a, b, c]); left.splice(i, 1); clipped = true; break;
    }
    if (!clipped) break;
  }
  if (left.length === 3) pieces.push(left);
  return pieces.filter(piece => areaOf(piece) > 1e-9);
}

/** Splits every cell the line crosses into its two sides. */
function split(cells: Vec2[][], line: Line): Vec2[][] {
  const out: Vec2[][] = [];
  for (const cell of cells) {
    const values = cell.map(p => value(line, p));
    if (values.every(v => v >= -EPS) || values.every(v => v <= EPS)) { out.push(cell); continue; }
    for (const keep of [1, -1] as const) { const piece = clipConvex(cell, line, keep); if (piece.length) out.push(piece); }
  }
  return out;
}

/** Where the slope's own coordinates put `p`: `a` across its width from its middle, `n` into the room from its wall. */
const local = (slope: CeilingSlope, p: Vec2) => {
  const { origin, right, inward } = slope.frame, d = { x: p.x - origin.x, z: p.z - origin.z };
  return { a: dot(d, right), n: dot(d, inward) };
};

/** The surface over `p`, and its height there. */
export function surfaceAt(ceiling: Pick<RoomCeiling, 'height' | 'slopes'>, p: Vec2): { surface: CeilingSurface; height: number } {
  let best: { surface: CeilingSurface; height: number } = { surface: FLAT, height: ceiling.height };
  ceiling.slopes.forEach((slope, index) => {
    const { a, n } = local(slope, p);
    if (n < -1e-6 || n > slope.reach + 1e-6 || Math.abs(a) > slope.frame.width / 2 + 1e-6) return;
    let height = slope.frame.knee + slope.rise * Math.max(0, n), surface: CeilingSurface = { kind: 'slope', slope: index };
    slope.raises.forEach((raise, k) => {
      if (a >= raise.a0 - 1e-6 && a <= raise.a1 + 1e-6 && raise.height > height) { height = raise.height; surface = { kind: 'raise', slope: index, raise: k }; }
    });
    if (height < best.height - 1e-9) best = { surface, height };
  });
  return best;
}

/** The ceiling's height over `p`. */
export const ceilingHeightAt = (ceiling: Pick<RoomCeiling, 'height' | 'slopes'>, p: Vec2): number => surfaceAt(ceiling, p).height;

/** A surface's height over `p`, as the plane it lies in. */
export function surfaceHeight(ceiling: Pick<RoomCeiling, 'height' | 'slopes'>, surface: CeilingSurface, p: Vec2): number {
  if (surface.kind === 'flat') return ceiling.height;
  const slope = ceiling.slopes[surface.slope];
  if (surface.kind === 'raise') return slope.raises[surface.raise].height;
  return slope.frame.knee + slope.rise * local(slope, p).n;
}

const sameSurface = (a: CeilingSurface, b: CeilingSurface) => a.kind === b.kind && (a.kind === 'flat'
  || (a.slope === (b as { slope: number }).slope && (a.kind !== 'raise' || a.raise === (b as { raise: number }).raise)));

/** The slope's ground plan: the rectangle of its width over its depth. */
function footprint(slope: CeilingSlope): Vec2[] {
  const { origin, right, inward, width } = slope.frame, depth = slope.reach;
  const at = (a: number, n: number) => ({ x: origin.x + right.x * a + inward.x * n, z: origin.z + right.z * a + inward.z * n });
  const corners = [at(-width / 2, 0), at(width / 2, 0), at(width / 2, depth), at(-width / 2, depth)];
  return signedArea(corners) < 0 ? corners.reverse() : corners;
}

/** Whether two convex polygons share some area. */
function overlap(a: readonly Vec2[], b: readonly Vec2[]): boolean {
  let piece: Vec2[] = [...a];
  for (let i = 0; i < b.length && piece.length; i++) {
    const p = b[i], q = b[(i + 1) % b.length], line = { c: -(p.x * (-(q.z - p.z)) + p.z * (q.x - p.x)), gx: -(q.z - p.z), gz: q.x - p.x };
    piece = clipConvex(piece, line, 1);
  }
  return piece.length >= 3 && areaOf(piece) > 1e-6;
}

/** The lines where the room's surfaces begin, end or meet. */
function lines(ceiling: Pick<RoomCeiling, 'height' | 'slopes'>): Line[] {
  const out: Line[] = [];
  const plane = (slope: CeilingSlope): Line => {
    const { origin, inward, knee } = slope.frame;
    return { c: knee - slope.rise * dot(origin, inward), gx: slope.rise * inward.x, gz: slope.rise * inward.z };
  };
  const shapes = ceiling.slopes.map(footprint);
  ceiling.slopes.forEach((slope, index) => {
    const { origin, right, inward, width, knee } = slope.frame, o = { r: dot(origin, right), i: dot(origin, inward) };
    // Across: f = a − a0; into the room: f = n − n0.
    const atA = (a: number): Line => ({ c: -o.r - a, gx: right.x, gz: right.z });
    const atN = (n: number): Line => ({ c: -o.i - n, gx: inward.x, gz: inward.z });
    out.push(atA(-width / 2), atA(width / 2), atN(0), atN(slope.reach));
    for (const raise of slope.raises) out.push(atA(raise.a0), atA(raise.a1), atN((raise.height - knee) / slope.rise));
    const own = plane(slope);
    ceiling.slopes.forEach((other, k) => {
      if (k === index || !overlap(shapes[index], shapes[k])) return;
      if (k > index) { const theirs = plane(other); out.push({ c: own.c - theirs.c, gx: own.gx - theirs.gx, gz: own.gz - theirs.gz }); }
      for (const raise of other.raises) out.push({ c: own.c - raise.height, gx: own.gx, gz: own.gz });
    });
  });
  return out.filter(line => Math.hypot(line.gx, line.gz) > 1e-9);
}

/** The room's mansards that slope its ceiling. */
function roomSlopes(level: Pick<Level, 'rooms' | 'walls' | 'openings' | 'items' | 'height'>, room: Room, tagsOf: TagsOf): CeilingSlope[] {
  const slopes: CeilingSlope[] = [];
  for (const item of level.items) {
    if (!isAttic(item) || !(item.size.h > .01) || !(item.size.d > .01) || !(item.size.w > .01)) continue;
    const frame = atticFrame(item, level.walls);
    if (!frame.wall || !frame.side || Math.abs(frame.top - room.height) > TOP_TOLERANCE) continue;
    // The room a step in front of its back, the smallest that holds it, as the wall plan finds a face's room.
    const probe = { x: frame.origin.x + frame.inward.x * .05, z: frame.origin.z + frame.inward.z * .05 };
    const holder = level.rooms.filter(value => value.polygon.length >= 3 && contains(value.polygon, probe)).sort((a, b) => a.area - b.area)[0];
    if (holder?.index !== room.index) continue;
    const rise = frame.height / frame.depth, wall = frame.wall, side = frame.side;
    // The project keeps the mansard's position to 0.1 mm and its turn to 1e-5 rad: square it to its wall's face, on it,
    // and its ends on the face's ends when they are within a centimetre, or the floor keeps hairline strips beside it.
    const sign = dot(frame.right, wall.dir) >= 0 ? 1 : -1, face = wallFace(level, wall, side);
    const along = dot({ x: frame.origin.x - wall.start.x, z: frame.origin.z - wall.start.z }, wall.dir);
    let lo = along - frame.width / 2, hi = along + frame.width / 2;
    if (Math.abs(lo - face.from) < .01) lo = face.from;
    if (Math.abs(hi - face.to) < .01) hi = face.to;
    const middle = (lo + hi) / 2, inward = { x: wall.normalIn.x * side, z: wall.normalIn.z * side }, shift = sign * (along - middle);
    const squared = { ...frame, width: hi - lo, inward, right: { x: wall.dir.x * sign, z: wall.dir.z * sign },
      origin: { x: wall.start.x + wall.dir.x * middle + inward.x * wall.thickness / 2, z: wall.start.z + wall.dir.z * middle + inward.z * wall.thickness / 2 } };
    const cuts = atticCuts(level, item, frame, tagsOf).map(cut => ({ ...cut, a0: cut.a0 + shift, a1: cut.a1 + shift }));
    slopes.push({ item, frame: squared as CeilingSlope['frame'], rise, reach: Math.max(0, (room.height - frame.knee) / rise), pitch: Math.atan(rise),
      raises: cuts.filter(cut => !cut.roof).map(cut => ({ openingId: cut.openingId,
        a0: Math.max(-squared.width / 2, cut.a0 - DORMER_FRAME), a1: Math.min(squared.width / 2, cut.a1 + DORMER_FRAME),
        height: Math.min(room.height, frame.knee + cut.y1 + DORMER_FRAME) })),
      roofWindows: cuts.filter(cut => cut.roof) });
  }
  return slopes;
}

/** Builds the room's ceiling; null when no mansard slopes it. */
export function buildRoomCeiling(level: Pick<Level, 'rooms' | 'walls' | 'openings' | 'items' | 'height'>, room: Room, tagsOf: TagsOf = id => cachedModelTags(id) ?? []): RoomCeiling | null {
  if (room.polygon.length < 3) return null;
  const slopes = roomSlopes(level, room, tagsOf);
  if (!slopes.length) return null;
  const floorOutline = roomFloorPolygon(level, room);
  const floor = signedArea(floorOutline) < 0 ? [...floorOutline].reverse() : floorOutline;
  const ceiling = { room, floor, height: room.height, slopes, cells: [] as CeilingCell[] };
  let pieces = convexPieces(floor);
  for (const line of lines(ceiling)) pieces = split(pieces, line);
  // What lines a hair apart leave between them is no part of the room: thinner than 0.2 mm.
  pieces = pieces.filter(piece => areaOf(piece) / Math.max(...piece.map((p, i) => { const q = piece[(i + 1) % piece.length]; return Math.hypot(q.x - p.x, q.z - p.z); })) > 2e-4);
  ceiling.cells = pieces.map(polygon => {
    const { surface } = surfaceAt(ceiling, centroid(polygon));
    if (surface.kind !== 'slope') return { polygon, surface, base: surfaceHeight(ceiling, surface, polygon[0]), gx: 0, gz: 0 };
    const slope = slopes[surface.slope], { origin, inward, knee } = slope.frame;
    return { polygon, surface, base: knee - slope.rise * dot(origin, inward), gx: slope.rise * inward.x, gz: slope.rise * inward.z };
  });
  return ceiling;
}

type CeilingLevel = Pick<Level, 'rooms' | 'walls' | 'openings' | 'items' | 'height'>;
const memo = new WeakMap<object, WeakMap<object, WeakMap<object, WeakMap<object, { version: number; byRoom: Map<number, RoomCeiling | null> }>>>>();

/** The room's ceiling as `buildRoomCeiling` gives it, kept while the level's rooms, walls, openings and items stay. */
export function roomCeiling(level: CeilingLevel, room: Room): RoomCeiling | null {
  if (!level.items.some(isAttic)) return null;
  const byItems = memo.get(level.items) ?? new WeakMap();
  memo.set(level.items, byItems);
  const byWalls = byItems.get(level.walls) ?? new WeakMap();
  byItems.set(level.walls, byWalls);
  const byRooms = byWalls.get(level.rooms) ?? new WeakMap();
  byWalls.set(level.rooms, byRooms);
  let entry = byRooms.get(level.openings);
  const version = modelTagsVersion();
  if (!entry || entry.version !== version) { entry = { version, byRoom: new Map() }; byRooms.set(level.openings, entry); }
  if (!entry.byRoom.has(room.index)) entry.byRoom.set(room.index, buildRoomCeiling(level, room));
  return entry.byRoom.get(room.index) ?? null;
}

/** Every room of the level whose ceiling a mansard slopes. */
export function slopedCeilings(level: CeilingLevel): RoomCeiling[] {
  if (!level.items.some(isAttic)) return [];
  return level.rooms.map(room => roomCeiling(level, room)).filter((value): value is RoomCeiling => !!value);
}

const cellHeight = (cell: CeilingCell, p: Vec2) => cell.base + cell.gx * p.x + cell.gz * p.z;

/** The floor's area with at least `height` of ceiling over it. */
export function areaWithHeadroom(ceiling: RoomCeiling, height: number): number {
  let area = 0;
  for (const cell of ceiling.cells) {
    if (Math.hypot(cell.gx, cell.gz) < 1e-12) { if (cell.base >= height - 1e-9) area += areaOf(cell.polygon); continue; }
    const piece = clipConvex(cell.polygon, { c: cell.base - height, gx: cell.gx, gz: cell.gz }, 1);
    if (piece.length) area += areaOf(piece);
  }
  return area;
}

/** The room's volume: the floor times the ceiling over it. */
export function ceilingVolume(ceiling: RoomCeiling): number {
  return ceiling.cells.reduce((sum, cell) => sum + areaOf(cell.polygon) * cellHeight(cell, centroid(cell.polygon)), 0);
}

/** Where the ceiling is exactly `height` high: pieces of line on the plan. */
export function headroomLine(ceiling: RoomCeiling, height: number): Array<[Vec2, Vec2]> {
  const out: Array<[Vec2, Vec2]> = [];
  for (const cell of ceiling.cells) {
    if (Math.hypot(cell.gx, cell.gz) < 1e-12) continue;
    const points: Vec2[] = [], f = (p: Vec2) => cellHeight(cell, p) - height;
    cell.polygon.forEach((p, i) => {
      const q = cell.polygon[(i + 1) % cell.polygon.length], fp = f(p), fq = f(q);
      if (Math.abs(fp) <= 1e-9) points.push(p);
      else if ((fp < 0 && fq > 1e-9) || (fp > 0 && fq < -1e-9)) { const t = fp / (fp - fq); points.push({ x: p.x + (q.x - p.x) * t, z: p.z + (q.z - p.z) * t }); }
    });
    const unique = points.filter((p, i) => points.findIndex(q => Math.hypot(p.x - q.x, p.z - q.z) < 1e-7) === i);
    if (unique.length >= 2) out.push([unique[0], unique[unique.length - 1]]);
  }
  return out;
}

/** A line of the plan where the ceiling changes surface: a hip, a valley, a fold, or — with `step` — a vertical face. */
export interface CeilingEdge {
  from: Vec2; to: Vec2; step: boolean;
  /** The surfaces on its two sides, and their heights at `from` and at `to`. */
  surfaces: [CeilingSurface, CeilingSurface];
  heights: [[number, number], [number, number]];
}

/** An order of the surfaces, so that a line between two is told from one side only. */
const rank = (surface: CeilingSurface) => surface.kind === 'flat' ? 0 : 1 + surface.slope * 1000 + (surface.kind === 'raise' ? 1 + surface.raise : 0);

/** The lines inside the room where one surface of its ceiling meets another. */
export function ceilingEdges(ceiling: RoomCeiling): CeilingEdge[] {
  const out: CeilingEdge[] = [];
  for (const cell of ceiling.cells) {
    cell.polygon.forEach((p, i) => {
      const q = cell.polygon[(i + 1) % cell.polygon.length], length = Math.hypot(q.x - p.x, q.z - p.z);
      if (length < 1e-4) return;
      // The cell is counter-clockwise: its outside is on the edge's right.
      const middle = { x: (p.x + q.x) / 2, z: (p.z + q.z) / 2 }, normal = { x: (q.z - p.z) / length, z: -(q.x - p.x) / length };
      const outside = { x: middle.x + normal.x * 1e-4, z: middle.z + normal.z * 1e-4 };
      if (!contains(ceiling.floor, outside)) return;
      const other = surfaceAt(ceiling, outside).surface;
      // Both cells along a line see it; the one whose surface ranks lower tells it, so it is told once.
      if (sameSurface(other, cell.surface) || rank(cell.surface) > rank(other)) return;
      const heights: CeilingEdge['heights'] = [[cellHeight(cell, p), surfaceHeight(ceiling, other, p)], [cellHeight(cell, q), surfaceHeight(ceiling, other, q)]];
      const step = Math.abs(heights[0][0] - heights[0][1]) > 1e-3 || Math.abs(heights[1][0] - heights[1][1]) > 1e-3;
      out.push({ from: p, to: q, step, surfaces: [cell.surface, other], heights });
    });
  }
  return out;
}

/** Whose a step is: the slope whose free end or niche's cheek it is — the lower side's where two slopes meet. −1 for none. */
export function stepOwner(edge: Pick<CeilingEdge, 'surfaces' | 'heights'>): number {
  const [first, second] = edge.surfaces;
  if (first.kind === 'flat') return second.kind === 'flat' ? -1 : second.slope;
  if (second.kind === 'flat' || first.slope === second.slope) return first.slope;
  return edge.heights[0][0] + edge.heights[1][0] <= edge.heights[0][1] + edge.heights[1][1] ? first.slope : second.slope;
}

/** The ceiling's surfaces by area: the flat part, and each slope's own with its niches and its steps. */
export function ceilingSurfaceAreas(ceiling: RoomCeiling): { flat: number; slopes: Array<{ item: Item; area: number }> } {
  const slopes = ceiling.slopes.map(slope => ({ item: slope.item, area: 0 }));
  let flat = 0;
  for (const cell of ceiling.cells) {
    const area = areaOf(cell.polygon) * Math.hypot(1, cell.gx, cell.gz);
    if (cell.surface.kind === 'flat') flat += area; else slopes[cell.surface.slope].area += area;
  }
  for (const edge of ceilingEdges(ceiling)) {
    if (!edge.step) continue;
    const length = Math.hypot(edge.to.x - edge.from.x, edge.to.z - edge.from.z);
    const face = length * (Math.abs(edge.heights[0][0] - edge.heights[0][1]) + Math.abs(edge.heights[1][0] - edge.heights[1][1])) / 2;
    const owner = stepOwner(edge);
    if (owner >= 0) slopes[owner].area += face;
  }
  return { flat, slopes };
}

/** The slope's finish, as the inspector's «Скат» sets it. */
export const slopeSurface = (slope: CeilingSlope): Surface | undefined => slope.item.materials?.slope ?? slope.item.materials?.['*'];

/** A stretch of the ceiling's height along a segment: from `t0` to `t1` metres along it, from height `h0` to `h1`. */
export interface ProfilePiece { t0: number; t1: number; h0: number; h1: number }

/**
 * The ceiling's height along the segment `a`→`b` (inside the floor): what a wall face along it reaches under the
 * ceiling. Pieces in order; where two meet at one point with different heights, the face steps there.
 */
export function profileAlong(ceiling: RoomCeiling, a: Vec2, b: Vec2): ProfilePiece[] {
  const length = Math.hypot(b.x - a.x, b.z - a.z);
  if (length < 1e-9) return [];
  const d = { x: (b.x - a.x) / length, z: (b.z - a.z) / length }, pieces: ProfilePiece[] = [];
  for (const cell of ceiling.cells) {
    let t0 = 0, t1 = length;
    const polygon = cell.polygon;
    for (let i = 0; i < polygon.length && t0 < t1; i++) {
      const p = polygon[i], q = polygon[(i + 1) % polygon.length];
      // Inside a counter-clockwise edge is its left: f(x) = cross(q − p, x − p) ≥ 0.
      const ex = q.x - p.x, ez = q.z - p.z, f0 = ex * (a.z - p.z) - ez * (a.x - p.x), df = ex * d.z - ez * d.x;
      if (Math.abs(df) < 1e-12) { if (f0 < -1e-9) t1 = -1; continue; }
      const t = -f0 / df;
      if (df > 0) t0 = Math.max(t0, t); else t1 = Math.min(t1, t);
    }
    if (t1 - t0 < 1e-6) continue;
    const at = (t: number) => ({ x: a.x + d.x * t, z: a.z + d.z * t });
    pieces.push({ t0, t1, h0: cellHeight(cell, at(t0)), h1: cellHeight(cell, at(t1)) });
  }
  return pieces.sort((p, q) => p.t0 - q.t0);
}

/** The area under the profile and under `cap`, between `from` and `to` along it. */
export function profileArea(pieces: readonly ProfilePiece[], cap = Infinity, from = -Infinity, to = Infinity, floor = 0): number {
  let area = 0;
  for (const piece of pieces) {
    const t0 = Math.max(piece.t0, from), t1 = Math.min(piece.t1, to);
    if (t1 - t0 < 1e-9) continue;
    const at = (t: number) => piece.h0 + (piece.h1 - piece.h0) * (t - piece.t0) / Math.max(piece.t1 - piece.t0, 1e-12);
    // The height above `floor` and under `cap`, integrated exactly: split where the line crosses either.
    const marks = [t0, t1];
    for (const level of [cap, floor]) {
      if (!Number.isFinite(level)) continue;
      const h0 = at(t0), h1 = at(t1);
      if ((h0 - level) * (h1 - level) < 0) marks.push(t0 + (level - h0) / (h1 - h0) * (t1 - t0));
    }
    marks.sort((p, q) => p - q);
    for (let i = 0; i + 1 < marks.length; i++) {
      const u0 = marks[i], u1 = marks[i + 1], clamp = (h: number) => Math.max(0, Math.min(h, cap) - floor);
      area += (clamp(at(u0)) + clamp(at(u1))) / 2 * (u1 - u0);
    }
  }
  return area;
}

/**
 * The lowest the ceilings come over a convex footprint on the plan: where something hung from the ceiling touches a
 * slope over it. Infinity where no sloped ceiling is over it.
 */
export function lowestCeilingOver(ceilings: readonly RoomCeiling[], footprint: readonly Vec2[]): number {
  let lowest = Infinity;
  const area = signedArea(footprint);
  if (Math.abs(area) < 1e-12) return lowest;
  const box = area < 0 ? [...footprint].reverse() : [...footprint];
  for (const ceiling of ceilings) for (const cell of ceiling.cells) {
    let piece: Vec2[] = box;
    for (let i = 0; i < cell.polygon.length && piece.length; i++) {
      const p = cell.polygon[i], q = cell.polygon[(i + 1) % cell.polygon.length], ex = q.x - p.x, ez = q.z - p.z;
      piece = clipConvex(piece, { c: ez * p.x - ex * p.z, gx: -ez, gz: ex }, 1);
    }
    for (const p of piece) lowest = Math.min(lowest, cellHeight(cell, p));
  }
  return lowest;
}

/** A stretch of a wall's top along it: from `u0` to `u1` metres from its start, from height `h0` to `h1`. */
export interface WallTopPiece { u0: number; u1: number; h0: number; h1: number }

/**
 * How high a wall stands along its length when a sloped ceiling of a room beside it comes down on it. Each side that
 * has a room holds it up to that room's ceiling over its face — a flat ceiling to its full height — and the wall is as
 * high as the higher of its sides, never higher than itself; a side with no room (the outside) holds nothing. Along its
 * ends, inside the walls it meets, it keeps the height its face has at the corner. Null when nothing brings it down.
 */
export function wallTop(level: Pick<Level, 'rooms' | 'walls'>, wall: Wall, ceilings: ReadonlyMap<number, RoomCeiling>): WallTopPiece[] | null {
  if (!ceilings.size) return null;
  const along = (p: Vec2) => (p.x - wall.start.x) * wall.dir.x + (p.z - wall.start.z) * wall.dir.z;
  const sides = new Map<1 | -1, WallTopPiece[]>();
  let sloped = false;
  for (const room of level.rooms) {
    if (room.polygon.length < 3) continue;
    const walls = edgeWalls(level, room);
    if (!walls.some(value => value?.id === wall.id)) continue;
    const floor = roomFloorPolygon(level, room), ceiling = ceilings.get(room.index);
    walls.forEach((value, index) => {
      if (value?.id !== wall.id) return;
      const p0 = floor[index], p1 = floor[(index + 1) % floor.length];
      const middle = { x: (p0.x + p1.x) / 2, z: (p0.z + p1.z) / 2 };
      const side: 1 | -1 = (middle.x - wall.center.x) * wall.normalIn.x + (middle.z - wall.center.z) * wall.normalIn.z >= 0 ? 1 : -1;
      const list = sides.get(side) ?? [];
      sides.set(side, list);
      const u0 = along(p0), u1 = along(p1);
      if (!ceiling) { list.push({ u0: Math.min(u0, u1), u1: Math.max(u0, u1), h0: room.height, h1: room.height }); return; }
      sloped = true;
      const inward = { x: wall.normalIn.x * side * 1e-6, z: wall.normalIn.z * side * 1e-6 }, sense = u1 >= u0 ? 1 : -1;
      for (const piece of profileAlong(ceiling, { x: p0.x + inward.x, z: p0.z + inward.z }, { x: p1.x + inward.x, z: p1.z + inward.z })) {
        const a = u0 + sense * piece.t0, b = u0 + sense * piece.t1;
        list.push(a <= b ? { u0: a, u1: b, h0: piece.h0, h1: piece.h1 } : { u0: b, u1: a, h0: piece.h1, h1: piece.h0 });
      }
    });
  }
  if (!sloped) return null;
  for (const list of sides.values()) list.sort((p, q) => p.u0 - q.u0);
  /** A side's height as a line over the stretch round `u`: its piece there, or the height at the nearest end it has. */
  const lineOf = (list: readonly WallTopPiece[], u: number): { a: number; k: number } => {
    const piece = list.find(value => value.u0 - 1e-9 <= u && u <= value.u1 + 1e-9);
    if (piece) { const k = (piece.h1 - piece.h0) / Math.max(piece.u1 - piece.u0, 1e-12); return { a: piece.h0 - k * piece.u0, k }; }
    let best = { distance: Infinity, height: 0 };
    for (const value of list) for (const [end, height] of [[value.u0, value.h0], [value.u1, value.h1]] as const)
      if (Math.abs(end - u) < best.distance) best = { distance: Math.abs(end - u), height };
    return { a: best.height, k: 0 };
  };
  const marks = new Set<number>([0, wall.length]);
  for (const list of sides.values()) for (const piece of list) { marks.add(piece.u0); marks.add(piece.u1); }
  const sorted = [...marks].filter(u => u >= -1e-9 && u <= wall.length + 1e-9).sort((p, q) => p - q);
  const pieces: WallTopPiece[] = [];
  for (let i = 0; i + 1 < sorted.length; i++) {
    const a = sorted[i], b = sorted[i + 1];
    if (b - a < 1e-7) continue;
    const middle = (a + b) / 2, lines = [...sides.values()].map(list => lineOf(list, middle));
    lines.push({ a: wall.height, k: 0 });
    // Higher of the sides, lower than the wall itself: split where any two of those lines cross.
    const cuts = [a, b];
    for (let j = 0; j < lines.length; j++) for (let k = j + 1; k < lines.length; k++) {
      const dk = lines[j].k - lines[k].k;
      if (Math.abs(dk) < 1e-12) continue;
      const u = (lines[k].a - lines[j].a) / dk;
      if (u > a + 1e-9 && u < b - 1e-9) cuts.push(u);
    }
    cuts.sort((p, q) => p - q);
    const height = (u: number) => Math.min(wall.height, Math.max(...lines.slice(0, -1).map(line => line.a + line.k * u)));
    for (let j = 0; j + 1 < cuts.length; j++) if (cuts[j + 1] - cuts[j] > 1e-9) pieces.push({ u0: cuts[j], u1: cuts[j + 1], h0: height(cuts[j]), h1: height(cuts[j + 1]) });
  }
  return pieces.some(piece => Math.min(piece.h0, piece.h1) < wall.height - 1e-6) ? pieces : null;
}
